/**
 * Celebration store — global trigger for XP celebrations (level-up Number Slam,
 * rank-up Badge Forge ceremony). One celebration shows at a time; extra
 * triggers queue. A rank-up drops any showing/queued level-ups (the ceremony
 * already communicates the new level). Rendered by `<CelebrationHost/>`.
 */

import { create } from 'zustand';

export interface LevelUpPayload {
  /** The level just reached. */
  level: number;
  /** XP earned into the new level. */
  xpCurrent: number;
  /** XP required to complete the new level. */
  xpTarget: number;
  /**
   * Optional 0..1 fill the XP bar begins from before animating to 100% (the player's
   * progress in the *old* level pre-earn). Omitted → the toast uses a near-full default
   * so the "completing the level" beat still reads. See specs/xp-live-feedback.
   */
  startProgress?: number;
}

export interface RankUpPayload {
  /** Previous rank (1-10). */
  fromRank: number;
  /** New rank (1-10). */
  toRank: number;
  /** The level just reached. */
  level: number;
  /** XP progress basis toward the next rank (caller decides the scale). */
  xpCurrent: number;
  xpTarget: number;
  /** Show the Share button (default true). */
  share?: boolean;
}

export type Celebration =
  | ({ type: 'levelUp'; id: number } & LevelUpPayload)
  | ({ type: 'rankUp'; id: number } & RankUpPayload);

interface CelebrationStoreState {
  current: Celebration | null;
  queue: Celebration[];
}

interface CelebrationStoreActions {
  showLevelUp: (payload: LevelUpPayload) => void;
  showRankUp: (payload: RankUpPayload) => void;
  dismiss: () => void;
}

let nextCelebrationId = 1;

export const useCelebrationStore = create<CelebrationStoreState & CelebrationStoreActions>(
  (set) => ({
    current: null,
    queue: [],

    showLevelUp: (payload: LevelUpPayload) =>
      set((s) => {
        const celebration: Celebration = { type: 'levelUp', id: nextCelebrationId++, ...payload };
        return s.current
          ? { queue: [...s.queue, celebration] }
          : { current: celebration };
      }),

    showRankUp: (payload: RankUpPayload) =>
      set((s) => {
        const celebration: Celebration = { type: 'rankUp', id: nextCelebrationId++, ...payload };
        const queueWithoutLevelUps = s.queue.filter((c) => c.type !== 'levelUp');
        // A showing level-up is superseded immediately; a showing rank-up finishes first.
        if (!s.current || s.current.type === 'levelUp') {
          return { current: celebration, queue: queueWithoutLevelUps };
        }
        return { queue: [...queueWithoutLevelUps, celebration] };
      }),

    dismiss: () =>
      set((s) => ({ current: s.queue[0] ?? null, queue: s.queue.slice(1) })),
  })
);
